import { Collapse, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconCirclePlus } from '@tabler/icons-react';
import { isDefined } from '@zorro/common-utils';
import {
  ComponentPropsWithoutRef,
  ReactElement,
  ReactNode,
  forwardRef,
  useState,
} from 'react';

import styles from './SidebarItem.module.css';

// ============================================================
// SidebarItem (top-level navigation row)
// ============================================================

export interface SidebarItemProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'style'
> {
  label: string;
  leftSection?: ReactElement;
  isActive?: boolean;
  /** Sub-items (SidebarItem.Sub). When provided, the chevron and collapse are automatic. */
  children?: ReactNode;
  /** Initial open state when children are provided (uncontrolled) */
  isOpenedByDefault?: boolean;
}

type SidebarItemComponent = React.ForwardRefExoticComponent<
  SidebarItemProps & React.RefAttributes<HTMLButtonElement>
> & {
  Sub: typeof SidebarSubitem;
};

const _SidebarItem = forwardRef<HTMLButtonElement, SidebarItemProps>(
  (
    {
      label,
      leftSection,
      isActive,
      disabled,
      className,
      onClick,
      children,
      isOpenedByDefault = false,
      ...props
    },
    ref
  ) => {
    const [opened, setOpened] = useState(isOpenedByDefault);
    const hasChildren = isDefined(children);
    const rootClassName = [styles.root, className].filter(isDefined).join(' ');

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (hasChildren) setOpened((prev) => !prev);
      onClick?.(event);
    };

    return (
      <div className={styles.wrapper}>
        <UnstyledButton
          ref={ref}
          className={rootClassName}
          data-active={isActive || undefined}
          data-disabled={disabled || undefined}
          disabled={disabled}
          onClick={handleClick}
          {...props}
        >
          <span className={styles.inner}>
            {leftSection && <span className={styles.icon}>{leftSection}</span>}
            <span className={styles.label}>{label}</span>
          </span>
          {hasChildren && (
            <span
              className={[
                styles.chevronButton,
                opened ? styles.chevronOpen : '',
              ]
                .filter(isDefined)
                .join(' ')}
            >
              <IconChevronDown size={16} stroke={2} />
            </span>
          )}
        </UnstyledButton>
        {hasChildren && (
          <Collapse expanded={opened}>
            <div className={styles.collapseContent}>{children}</div>
          </Collapse>
        )}
      </div>
    );
  }
) as SidebarItemComponent;

_SidebarItem.displayName = 'SidebarItem';

// ============================================================
// SidebarSubitem (indented child row)
// ============================================================

export interface SidebarSubitemProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'style'
> {
  title: string;
  description?: string;
  isCentered?: boolean;
  isActive?: boolean;
  /** Optional right section slot (e.g. SmallStatusIcon badge) */
  rightSection?: ReactNode;
}

export const SidebarSubitem = forwardRef<
  HTMLButtonElement,
  SidebarSubitemProps
>(
  (
    {
      title,
      description,
      isCentered,
      isActive,
      disabled,
      rightSection,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const rootClassName = [
      styles.subRoot,
      isCentered ? styles.subRootCreateNew : null,
      className,
    ]
      .filter(isDefined)
      .join(' ');

    return (
      <UnstyledButton
        ref={ref}
        className={rootClassName}
        data-active={isActive || undefined}
        data-disabled={disabled || undefined}
        data-with-description={description ? true : undefined}
        disabled={disabled}
        onClick={onClick}
        {...props}
      >
        {isCentered ? (
          <>
            <span className={styles.centered}>
              <IconCirclePlus size={20} stroke={2} />
            </span>
            <span className={styles.subTitle}>{title}</span>
          </>
        ) : (
          <>
            <span className={styles.subText}>
              <span className={styles.subTitle}>{title}</span>
              {description && (
                <span className={styles.subDesc}>{description}</span>
              )}
            </span>
            {rightSection && (
              <span className={styles.subRightSection}>{rightSection}</span>
            )}
          </>
        )}
      </UnstyledButton>
    );
  }
);

SidebarSubitem.displayName = 'SidebarSubitem';

_SidebarItem.Sub = SidebarSubitem;

export const SidebarItem = _SidebarItem;
